import { useIsMobile } from '@lib/hooks'
import {
  type BlockWireframe,
  Factory,
  type ID,
  ProjectActions,
} from '@octopus-do/octopus-project-core'
import { thunkUpdateBlockTitleNoCommit } from '@store/migrationActions/thunkActions/thunkBlocksActions'
import { migrationProjectSelector } from '@store/migrationSelectors/projectSelectors'
import { AIActions } from '@store/reducers/ai'
import { artBoardActions } from '@store/reducers/artboard'
import { managerActions } from '@store/reducers/manager/actions'
import type { AppState } from '@store/types'
import { getId } from '@store/utils'
import {
  rollbackToBackupSnapshot,
  saveBackupSnapshot,
} from '@storeOld/actionCreators'
import { useDispatch, useSelector } from 'react-redux'
import type { Action } from 'redux'
import type { ThunkDispatch } from 'redux-thunk'

import { AI_ATTEMPTS, VITE_AI_HOST } from '@/constants/env'
import { PROJECT_HISTORY_INIT } from '@/store/reducers/manager/middleware/history/types'
import { ProjectOrigin } from '@/types'
import { parseCsv, postEventStreamV2 } from '@/utils/streamsV2'
import { getPageNameFromPathname } from '@/utils/strings'
import {
  addFilledChild,
  addFooter,
  addGeneratedSection,
  addHeader,
  addServicePages,
  clearAiNotifications,
  notifyAiGeneratingFinished,
  notifyAiInProgress,
  notifySomethingWentWrong,
  saveGeneratedPages,
  saveGeneratedSection,
  setNodeSeoDescription,
  setNodeSeoH1,
  setNodeSeoTitle,
  setNodeTitle,
} from './actionCreators'
import { ColumnsV2 } from './types'
import {
  NodesMap,
  parseBlockWireframeAndText,
  prepareContext,
  removeQuotes,
  sectionTitleFromTheme,
} from './utils'

type GenerateProject = (theme: string) => Promise<void>

/**
 * Where the generated pages go. No target means a brand-new project.
 */
export type GenerateTarget =
  // Child pages under a page that already exists (the page's "Generate pages" prompt).
  | { parentId: ID }
  // A new section of the open project, appended to the active tab (OCT-5102) — what
  // AI Generate in the project's AI menu does, the way File → Import does.
  | { newSection: true }

const useGenerateProject = (target?: GenerateTarget): GenerateProject => {
  const dispatch = useDispatch<ThunkDispatch<AppState, unknown, Action>>()

  const parentId = target && 'parentId' in target ? target.parentId : null
  const intoNewSection = Boolean(target && 'newSection' in target)
  // Both targets add to a project that is open and saved already; only a bare call starts
  // one from nothing, and only that call may reset state or save the project as a whole.
  const intoOpenProject = Boolean(parentId) || intoNewSection

  // The assistant sidebar is 360px wide and nearly full-height: on a phone it
  // covers the whole canvas, so the sitemap being generated is invisible until
  // the panel is closed by hand (OCT-4968). Keep it closed while generating —
  // the footer button opens it when the user actually wants the chat.
  const isMobile = useIsMobile()

  const defaultColor = useSelector(
    migrationProjectSelector.getProjectDefaultColorId
  )

  const context = useSelector(
    (state: AppState) => parentId && prepareContext(state, parentId)
  )
  const parentNode = useSelector(
    (state: AppState) =>
      parentId && migrationProjectSelector.getProjectNodeById(parentId)(state)
  )

  return async (theme) => {
    let mapPathToNodeId: NodesMap = new NodesMap()
    let blockNumbers: number[] = []
    let nodeIds: string[] = []
    let childIds: string[] = []
    let currentNodeId: string | null = null
    let currentBlockId: string | null = null
    // The section the pages land in when the target is a new section; null otherwise.
    let sectionId: ID | null = null

    let abortController: (() => void) | null = null

    // Called from the stream's 'attempt' handler, which fires on the first attempt too —
    // always right after the rollback, so each attempt works in a section of its own.
    const startSection = () => {
      if (intoNewSection) {
        sectionId = dispatch(addGeneratedSection(sectionTitleFromTheme(theme)))
      }
    }

    const cleanup = () => {
      if (abortController) {
        window.removeEventListener('CancelAiGeneration', handleCancel)
        abortController = null
      }
    }

    const handleCancel = () => {
      dispatch(clearAiNotifications())

      if (abortController) {
        abortController()
      }
    }

    const handlePathName = (value: string, terminal: boolean) => {
      if (terminal) {
        currentNodeId && dispatch(addFooter(currentNodeId))

        currentNodeId = null
        currentBlockId = null

        const fullPath = `${value.startsWith('/') ? '' : '/'}${value}`
        if (mapPathToNodeId.has(fullPath)) {
          // biome-ignore lint/suspicious/noConsole: intentional
          console.log(`Skipped duplicate page: ${fullPath}`)
          return
        }

        addPage(fullPath)

        dispatch(addHeader(currentNodeId!))
      }
    }

    const handleTitle = (value: string, terminal: boolean) => {
      dispatch(setNodeTitle(currentNodeId!, value))
      if (terminal) {
        dispatch(setNodeSeoTitle(currentNodeId!, value))
      }
    }

    const handleDescription = (value: string, terminal: boolean) => {
      if (terminal) {
        dispatch(setNodeSeoDescription(currentNodeId!, value))
      }
    }

    const handleH1 = (value: string, terminal: boolean) => {
      if (terminal) {
        dispatch(setNodeSeoH1(currentNodeId!, value))
      }
    }

    const handleBlockTitle = (
      value: string,
      rowNumber: number,
      _finished: boolean
    ) => {
      const parsedBlock = parseBlockWireframeAndText(value)
      if (!parsedBlock || !currentNodeId) {
        return
      }

      const { wireframe, text: title } = parsedBlock

      if (currentBlockId) {
        dispatch(
          thunkUpdateBlockTitleNoCommit({
            id: currentBlockId,
            title,
          })
        )
        return
      }

      currentBlockId = getId()

      const block = Factory.block.monolithic({
        colorId: defaultColor,
        title,
        visual: {
          type: 'wireframes',
          wireframes: [wireframe] as BlockWireframe[],
        },
        id: currentBlockId,
      })

      dispatch(
        ProjectActions.blocks.add({
          items: [block],
          index: blockNumbers[rowNumber - 1] + 1,
          parents: [currentNodeId],
        })
      )
    }

    const handleMessage = ({
      rowNumber,
      columnName,
      value,
      terminal,
    }: {
      rowNumber: number
      columnName: string
      value: string
      terminal: boolean
    }): void => {
      if (!rowNumber) {
        return
      }

      value = removeQuotes(value).trim()

      blockNumbers[rowNumber - 1] = blockNumbers[rowNumber - 1] || 0

      if (!value || value === '-') {
        return
      }

      if (columnName === ColumnsV2.PATHNAME) {
        if (rowNumber === 2 && !blockNumbers[0]) {
          if (currentNodeId) {
            dispatch(ProjectActions.nodes.delete({ ids: [currentNodeId] }))
          }

          throw new Error('First page is empty')
        }
        handlePathName(value, terminal)
        return
      }

      if (!currentNodeId) {
        return
      }

      switch (columnName) {
        case ColumnsV2.TITLE:
          handleTitle(value, terminal)
          break
        case ColumnsV2.DESCRIPTION:
          handleDescription(value, terminal)
          break
        case ColumnsV2.H1:
          handleH1(value, terminal)
          break
        case ColumnsV2.BLOCKS:
          handleBlockTitle(value, rowNumber, terminal)
          if (terminal) {
            if (rowNumber === 1 && value === 'null') {
              dispatch(ProjectActions.nodes.delete({ ids: [currentNodeId] }))
              throw new Error('First block is null')
            }
            currentBlockId = null
            blockNumbers[rowNumber - 1]++
          }
          break
        default:
      }
    }

    const addPage = (pathname: string): void => {
      const pathnameParts = pathname.split('/').filter(Boolean)

      let parentSlug = ''
      if (parentId) {
        // @TODO review this and fix
        // @ts-expect-error - Property 'notes' does not exist on type
        parentSlug = parentNode!.notes?.seoSlug ?? ''
      }

      // Finding the youngest ancestor
      const { youngestAncestor, pathPartsNumber } =
        mapPathToNodeId.getYoungestAncestor(pathname) || {
          youngestAncestor: parentId,
          pathPartsNumber: -1,
        }

      let currentAncestor = youngestAncestor

      if (currentAncestor && currentAncestor !== parentId) {
        // Creating ancestor chain for each part of path between pathname & youngest ancestor
        for (let i = pathPartsNumber; i < pathnameParts.length - 1; i++) {
          currentNodeId = getId()

          const slug = pathnameParts.slice(0, i + 1).join('/')

          mapPathToNodeId!.set(slug, currentNodeId)

          dispatch(
            addFilledChild({
              id: currentNodeId,
              parentId: currentAncestor,
              title: getPageNameFromPathname(pathnameParts[i]),
              slug: parentSlug + slug,
            })
          )

          if (parentId === currentAncestor) {
            childIds.push(currentNodeId)
          }

          nodeIds.push(currentNodeId)

          currentAncestor = currentNodeId
        }
      }

      currentNodeId = getId()

      mapPathToNodeId!.set(pathname, currentNodeId)

      dispatch(
        addFilledChild({
          id: currentNodeId,
          // Without an ancestor a page is a root: of the new section when there is one,
          // else of the tab's first section (addFilledChild's own default).
          parentId: currentAncestor ?? sectionId,
          title: ' ',
          slug: parentSlug + pathname,
        })
      )

      if (currentAncestor === parentId) {
        childIds.push(currentNodeId)
      }
      nodeIds.push(currentNodeId!)
    }

    const finishAndSave = async () => {
      cleanup()

      if (currentNodeId) {
        dispatch(addFooter(currentNodeId))
      }

      // Cookies and 404 belong to a project, not to one section of it: a project that is
      // already open either has them or has decided not to.
      if (!intoOpenProject) {
        const servicePagesIds = (await dispatch(
          addServicePages()
        )) as unknown as string[]
        nodeIds.push(...servicePagesIds)
      }

      dispatch(artBoardActions.setAiGeneratingStatus({ status: false }))
      dispatch(AIActions.stopMessage())

      if (intoNewSection) {
        // Nothing arrived — cancelled at once, or every row was junk. An empty section
        // committed to the project would be the only trace, so take it back out instead.
        if (!sectionId || nodeIds.length === 0) {
          dispatch(rollbackToBackupSnapshot())
          return
        }

        dispatch(
          AIActions.addMessage({
            message: {
              type: 'assistant',
              text: 'I’ve added a new section based on your description. What would you like to do next?',
            },
          })
        )

        dispatch(saveGeneratedSection(sectionId, nodeIds))

        // The section lands at the end of the tab, which on any project with something
        // in it is off screen: without this the generation reads as having done nothing.
        dispatch(artBoardActions.revealSection({ id: sectionId }))
        return
      }

      dispatch(
        AIActions.addMessage({
          message: {
            type: 'assistant',
            text: 'I’ve created a project based on your description. What would you like to do next?',
          },
        })
      )

      // @TODO review this and fix
      // @ts-expect-error - Expected 1-2 arguments, but got 3
      dispatch(saveGeneratedPages(nodeIds, parentId, childIds))

      // generationTheme из URL уберёт push('/{projectId}') в saveProjectSaga:
      // отдельная навигация здесь открывала бы окно, в котором MainRoot
      // считает /ai-sitemap/new-project без темы мусорным и уводит на дашборд
      dispatch(
        managerActions.saveProjectSaga({
          ignoreAuthenticated: true,
          origin: ProjectOrigin.AI,
        })
      )
    }

    if (!intoOpenProject) {
      dispatch(managerActions.initEmptyProjectActionsSaga())
      dispatch({ type: PROJECT_HISTORY_INIT })
    }

    if (parentId) {
      mapPathToNodeId.set('/', parentId)
    }

    dispatch(
      AIActions.startChat({
        message: theme,
        keepSidebarClosed: isMobile,
      })
    )

    dispatch(artBoardActions.setAiGeneratingStatus({ status: true }))
    dispatch(AIActions.setGenerating(true))
    dispatch(saveBackupSnapshot())

    const [stream, abort] = postEventStreamV2(
      parentId
        ? `${VITE_AI_HOST}/v2/generate-subtree`
        : `${VITE_AI_HOST}/v2/generate-project`,
      {
        theme,
        ...(parentId
          ? {
              context,
            }
          : {
              unwantedPages: ['Cookies', '404'],
            }),
      },
      { attempts: AI_ATTEMPTS }
    )

    abortController = abort

    window.addEventListener('CancelAiGeneration', handleCancel)

    dispatch(
      notifyAiInProgress(() => {
        // @todo review a state mutation problem
        abort()
      })
    )

    const parsedStream = parseCsv(stream)
    parsedStream.on('cell', ({ rowNumber, value, colNumber, terminal }) =>
      handleMessage({
        rowNumber,
        value,
        columnName:
          Object.values(ColumnsV2)[
            Math.min(colNumber, Object.values(ColumnsV2).length - 1)
          ],
        terminal,
      })
    )

    stream.on('error', (e) => {
      // biome-ignore lint/suspicious/noConsole: intentional
      console.log('Error ', e)
    })

    stream.on('abort', finishAndSave)

    stream.on('attemptsWasted', () => {
      // A half-built section in a project that was fine before is worse than nothing;
      // the new-project path has nothing to protect and keeps what it got.
      if (intoNewSection) {
        dispatch(rollbackToBackupSnapshot())
      }

      dispatch(clearAiNotifications())
      dispatch(notifySomethingWentWrong())
      dispatch(artBoardActions.setAiGeneratingStatus({ status: false }))
      dispatch(AIActions.setGenerating(false))
      dispatch(AIActions.setError('unknown'))
    })

    stream.on('attempt', (attemptNumber) => {
      // biome-ignore lint/suspicious/noConsole: intentional
      console.log(`Attempt: ${attemptNumber}`)
      dispatch(rollbackToBackupSnapshot())
      mapPathToNodeId = new NodesMap()
      blockNumbers = []
      nodeIds = []
      childIds = []
      currentNodeId = null
      currentBlockId = null
      startSection()
    })

    stream.on('success', () => {
      dispatch(clearAiNotifications())
      dispatch(AIActions.setGenerating(false))
      dispatch(notifyAiGeneratingFinished())
      setTimeout(() => {
        dispatch(clearAiNotifications())
      }, 8000)
      finishAndSave()
    })
  }
}

export default useGenerateProject
